import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  getTypeDefinition,
  isScalarType,
  schemaHasContent,
} from "../../utils/graphqlSchemaParser";

function TypeBadge({ kind }) {
  const colors = {
    OBJECT: { bg: "#eff6ff", color: "#0066cc" },
    INPUT: { bg: "#f0fdf4", color: "#15803d" },
    ENUM: { bg: "#fef3c7", color: "#b45309" },
    FIELD: { bg: "#f3f4f6", color: "#374151" },
    ARG: { bg: "#fafafa", color: "#6b7280" },
    SCALAR: { bg: "#f5f3ff", color: "#6d28d9" },
  };
  const style = colors[kind] || colors.FIELD;
  return (
    <span className="schema-badge" style={{ background: style.bg, color: style.color }}>
      {kind}
    </span>
  );
}

function TypeFieldsTree({
  typeName,
  schemaTree,
  depth = 0,
  maxDepth = 4,
  onInsertField,
  onFocusType,
  path = "",
}) {
  if (!typeName || depth >= maxDepth) return null;

  if (isScalarType(typeName)) {
    return (
      <TreeNode
        label={typeName}
        meta="scalar"
        kind="SCALAR"
        depth={depth}
        onSelect={() => onFocusType?.(typeName)}
      />
    );
  }

  const typeDef = getTypeDefinition(schemaTree, typeName);
  if (!typeDef) {
    return (
      <TreeNode
        label={typeName}
        meta="tipo externo"
        kind="OBJECT"
        depth={depth}
        onSelect={() => onFocusType?.(typeName)}
      />
    );
  }

  if (typeDef.kind === "ENUM") {
    return (
      <TreeNode
        label={typeDef.name}
        meta={typeDef.values?.join(" · ")}
        kind="ENUM"
        depth={depth}
        defaultOpen={depth < 1}
        onSelect={() => onFocusType?.(typeDef.name)}
      />
    );
  }

  const fields = typeDef.fields || [];
  if (!fields.length) return null;

  return (
    <TreeNode
      label={typeDef.name}
      meta={`${fields.length} fields`}
      kind={typeDef.kind}
      depth={depth}
      defaultOpen={depth < 1}
      onSelect={() => onFocusType?.(typeDef.name)}
    >
      {fields.map((field) => {
        const childPath = `${path}.${field.name}`;
        const nestedScalar = isScalarType(field.baseType);
        return (
          <React.Fragment key={childPath}>
            <TreeNode
              label={field.name}
              meta={field.type}
              kind="FIELD"
              depth={depth + 1}
              onSelect={() => onInsertField?.(field.name)}
            />
            {!nestedScalar && field.baseType && (
              <TypeFieldsTree
                typeName={field.baseType}
                schemaTree={schemaTree}
                depth={depth + 2}
                maxDepth={maxDepth}
                onInsertField={onInsertField}
                onFocusType={onFocusType}
                path={childPath}
              />
            )}
          </React.Fragment>
        );
      })}
    </TreeNode>
  );
}

function TreeNode({
  label,
  meta,
  kind,
  children,
  defaultOpen = false,
  onSelect,
  depth = 0,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = Boolean(children);

  return (
    <>
      <ListItemButton
        className="schema-tree-item"
        sx={{ pl: 1 + depth * 1.25, py: 0.35 }}
        onClick={() => onSelect?.()}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            sx={{ mr: 0.5, p: 0.25 }}
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}
        <ListItemText
          primary={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <code className="schema-name">{label}</code>
              {kind && <TypeBadge kind={kind} />}
            </Box>
          }
          secondary={meta ? <span className="schema-meta">{meta}</span> : null}
        />
      </ListItemButton>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

function filterFields(fields, q) {
  if (!q) return fields;
  const lower = q.toLowerCase();
  return (fields || []).filter(
    (f) =>
      f.name.toLowerCase().includes(lower) ||
      f.type.toLowerCase().includes(lower) ||
      f.baseType?.toLowerCase().includes(lower)
  );
}

function filterTypes(types, q) {
  if (!q) return types;
  const lower = q.toLowerCase();
  return (types || []).filter(
    (t) =>
      t.name.toLowerCase().includes(lower) ||
      t.fields?.some((f) => f.name.toLowerCase().includes(lower))
  );
}

export default function SchemaBrowser({
  schemaTree,
  onInsertField,
  onSelectOperation,
  sdlSource,
}) {
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("query");
  const [focusedType, setFocusedType] = useState(null);

  const hasContent = schemaHasContent(schemaTree);

  const filtered = useMemo(
    () => ({
      queryFields: filterFields(schemaTree?.queryFields, search),
      mutationFields: filterFields(schemaTree?.mutationFields, search),
      types: filterTypes(schemaTree?.types, search),
      inputs: filterTypes(schemaTree?.inputs, search),
      enums: filterTypes(schemaTree?.enums, search),
    }),
    [schemaTree, search]
  );

  if (!schemaTree) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Cargando schema…
      </Typography>
    );
  }

  if (schemaTree.error) {
    return (
      <Alert severity="error" sx={{ m: 1.5 }}>
        Error al parsear schema: {schemaTree.error}
      </Alert>
    );
  }

  if (!hasContent) {
    return (
      <Alert severity="warning" sx={{ m: 1.5 }}>
        Schema vacío. Verificá que la lambda exponga <code>getApiSchema</code> o recargá la página.
      </Alert>
    );
  }

  return (
    <Box className="schema-browser">
      {sdlSource === "bundled" && (
        <Alert severity="info" sx={{ mb: 1, py: 0, fontSize: 12 }}>
          Schema cargado desde bundle local (API sin SDL).
        </Alert>
      )}

      <TextField
        size="small"
        fullWidth
        placeholder="Buscar tipos o campos…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Box className="schema-section-tabs">
        {[
          { id: "query", label: `Query (${schemaTree.queryFields.length})` },
          { id: "mutation", label: `Mutation (${schemaTree.mutationFields.length})` },
          { id: "types", label: `Types (${schemaTree.types.length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`schema-section-tab ${section === tab.id ? "active" : ""}`}
            onClick={() => setSection(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </Box>

      {focusedType && (
        <Box className="schema-focused-type">
          <Typography variant="caption" color="text.secondary">
            Tipo seleccionado
          </Typography>
          <code>{focusedType}</code>
          <button type="button" onClick={() => setFocusedType(null)}>
            ✕
          </button>
        </Box>
      )}

      <List dense disablePadding className="schema-tree">
        {section === "query" &&
          filtered.queryFields.map((field) => (
            <TreeNode
              key={field.name}
              label={field.name}
              meta={field.type}
              kind="FIELD"
              defaultOpen={search.length > 0}
              onSelect={() => onSelectOperation?.(field, "QUERY")}
            >
              {field.args?.length > 0 && (
                <Box component="div" className="schema-subgroup">
                  <Typography variant="caption" sx={{ pl: 3, color: "text.secondary" }}>
                    Arguments
                  </Typography>
                  {field.args.map((arg) => (
                    <TreeNode
                      key={arg.name}
                      label={arg.name}
                      meta={arg.type}
                      kind="ARG"
                      depth={1}
                      onSelect={() => onInsertField?.(arg.name)}
                    />
                  ))}
                </Box>
              )}
              {!isScalarType(field.baseType) && (
                <Box component="div" className="schema-subgroup">
                  <Typography variant="caption" sx={{ pl: 3, color: "text.secondary" }}>
                    Returns {field.baseType}
                  </Typography>
                  <TypeFieldsTree
                    typeName={field.baseType}
                    schemaTree={schemaTree}
                    depth={1}
                    onInsertField={onInsertField}
                    onFocusType={setFocusedType}
                    path={field.name}
                  />
                </Box>
              )}
            </TreeNode>
          ))}

        {section === "mutation" &&
          filtered.mutationFields.map((field) => (
            <TreeNode
              key={field.name}
              label={field.name}
              meta={field.type}
              kind="FIELD"
              defaultOpen={search.length > 0}
              onSelect={() => onSelectOperation?.(field, "MUTATION")}
            >
              {field.args?.map((arg) => (
                <TreeNode
                  key={arg.name}
                  label={arg.name}
                  meta={arg.type}
                  kind="ARG"
                  depth={1}
                  onSelect={() => onInsertField?.(arg.name)}
                />
              ))}
              {!isScalarType(field.baseType) && field.baseType && (
                <TypeFieldsTree
                  typeName={field.baseType}
                  schemaTree={schemaTree}
                  depth={1}
                  onInsertField={onInsertField}
                  onFocusType={setFocusedType}
                  path={field.name}
                />
              )}
            </TreeNode>
          ))}

        {section === "types" && (
          <>
            {filtered.types.map((type) => (
              <TypeFieldsTree
                key={type.name}
                typeName={type.name}
                schemaTree={schemaTree}
                depth={0}
                onInsertField={onInsertField}
                onFocusType={setFocusedType}
              />
            ))}
            {filtered.inputs.map((type) => (
              <TreeNode
                key={type.name}
                label={type.name}
                meta={`input · ${type.fields?.length || 0}`}
                kind="INPUT"
                defaultOpen={Boolean(search)}
                onSelect={() => setFocusedType(type.name)}
              >
                {type.fields?.map((field) => (
                  <TreeNode
                    key={field.name}
                    label={field.name}
                    meta={field.type}
                    kind="FIELD"
                    depth={1}
                    onSelect={() => onInsertField?.(field.name)}
                  />
                ))}
              </TreeNode>
            ))}
            {filtered.enums.map((type) => (
              <TreeNode
                key={type.name}
                label={type.name}
                meta={type.values?.join(" · ")}
                kind="ENUM"
                defaultOpen={Boolean(search)}
                onSelect={() => setFocusedType(type.name)}
              />
            ))}
          </>
        )}
      </List>
    </Box>
  );
}
